<template>
  <v-container class="pa-0" height="100%">
    <MessageDialog
      :show-dialog="showPasswordHasChangedDialog"
      :has-button-two="false"
      type="success"
      :title="$t('L_password_has_been_updated')"
      :message="
        $t('L_will_now_be_logged_out_and_you_will_have_to_log_back_in_using_your_new_password')
      "
      :button-one-label="$t('L_OK')"
      :is-persistent="true"
      @buttonOneAction="startLogoff"
    />

    <CardTemplate id="update_password_card" @show-modal="showDialog">
      <template #icon>
        <v-img
          class="account-img"
          src="./assets/update-password.svg"
          alt=""
          role="presentation"
          width="46px"
          height="46px"
        />
      </template>
      <template #title>
        {{ $t('L_update_password') }}
      </template>
      <template #content>
        <p :class="!$vuetify.display.xs ? 'd-flex align-center justify-center mt-4' : 'd-none'">
          ***********
        </p>
      </template>
      <template #action>
        <OnexButton
          @click="showDialog"
          variant="text"
          id="update_password_trigger"
          :aria-label="$t('L_update_password')"
        >
          {{ $t('L_edit') }}
        </OnexButton>
      </template>
    </CardTemplate>

    <MyAccountDialog
      :header="$t('L_update_password')"
      :show="showUpdatePasswordDialog"
      @save="updatePassword"
      @close="closeDialog"
      :close-is-active="closeIsActive"
      :enable-save-button="enableSaveButton"
      save-changes-btn-id="save_user_password"
      close-btn-id="close_user_password"
      close-btn-class="update_password_close"
      modal-class="update_password_modal"
    >
      <v-form id="form-user-password" ref="formUserPassword" v-model="formUserPasswordValid">
        <v-row>
          <v-col cols="12">
            <onex-password
              :label="$t('L_enter_current_password')"
              required
              v-model="currentPassword"
              data-comp-id="enter_current_password"
            />
          </v-col>
          <v-col cols="12">
            <PasswordForm
              v-model="password"
              :password-label="$t('L_enter_new_password')"
              :confirm-password-label="$t('L_confrim_new_password')"
              :email="customerProfile.email"
              new-pass-id="enter_new_password"
              confirm-pass-id="confirm_new_password"
              @update:modelValue="onPasswordInput"
              :show-tool-tip="false"
            />
          </v-col>
        </v-row>
      </v-form>
    </MyAccountDialog>
  </v-container>
</template>
<script lang="ts">
import PasswordForm from '@/components/PasswordForm/PasswordForm.vue';
import { PasswordInput } from '@/components/Inputs';
import MessageDialog from '@/components/MessageDialog.vue';
import { defineComponent, type ComponentOptions } from 'vue';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { mapState, mapStores } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { useVuelidate } from '@vuelidate/core';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { useHomeStore } from '@/modules/home/store/home.store';
import MyAccountDialog from './MyAccountDialog.vue';
import CardTemplate from './CardTemplate.vue';
import { mdiCheckCircleOutline } from '@mdi/js';
import { OnexButton, OnexPassword } from '@primerica/onex-ui-lib';
import { RouteNames } from '@/router/routeNames';

const componentOptions: ComponentOptions = {
  name: 'UpdatePassword',
  components: {
    CardTemplate,
    MyAccountDialog,
    PasswordForm,
    PasswordInput,
    MessageDialog,
    OnexButton,
    OnexPassword,
  },
  data() {
    return {
      password: '' as string | undefined,
      confirmPassword: '',
      currentPassword: '',
      showUpdatePasswordDialog: false,
      closeIsActive: false,
      showPasswordHasChangedDialog: false,
      v$: useVuelidate(),
      mdiCheckCircleOutline,
      formUserPasswordValid: false,
    };
  },
  computed: {
    ...mapStores(useGlobalStore, usePolicyStore, useHomeStore),
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    enableSaveButton() {
      return this.v$.$anyDirty && !this.v$.$invalid && this.formUserPasswordValid;
    },
  },
  methods: {
    setCloseIsActive() {
      this.closeIsActive = true;
    },
    // TODO; please suggest to QA to test properly
    // enableSaveButton(newValue: boolean) {
    //   this.closeIsActive = !newValue;
    // },
    onResponse() {
      /*
       * Emit custom actions when user successfully saves changes to: Password
       * https://www7.v1host.com/Primerica/attachment.img/284635/Analytics%20User%20Stories%20DT_3.30.21.docx
       */
      const i = this.$dynatrace.enterAction('account_settings_changes_saved_password');
      this.$dynatrace.leaveAction(i);
      try {
        this.clearForm();
        setTimeout(() => this.setCloseIsActive(), 500);
      } catch (_error) {
        /* empty */
      }
    },
    showDialog() {
      this.showUpdatePasswordDialog = true;
    },
    onPasswordInput(password: string) {
      if (!password || !this.v$.password) {
        return;
      }
      this.v$.password.$model = password;
    },
    closeDialog() {
      this.showUpdatePasswordDialog = false;
      this.clearForm();
    },
    clearForm() {
      this.password = undefined;
      this.currentPassword = '';
      window.postMessage('form-reset', `${window.location.protocol}//${window.location.hostname}`);
      this.v$.$reset();
    },
    async updatePassword() {
      const updatePassword = {
        username: this.customerProfile.email,
        oldPassword: this.currentPassword,
        newPassword: this.password,
        firstName: this.customerProfile.firstName,
        lastName: this.customerProfile.lastName,
        locale: this.locale,
      };
      if (updatePassword.username) {
        try {
          await this.homeStore.updatePassword(updatePassword);
        } catch (error) {
          // TODO: handle error case properly, for now just log to console and show generic error message
          this.$globalStore.addAlert({
            type: 'error',
            details: {
              key: 'L_error_updating_password',
            },
          });
          throw error;
        }
        this.onResponse();
        this.showPasswordHasChangedDialog = true;
      }
    },
    async startLogoff() {
      this.showPasswordHasChangedDialog = false;
      if (CapacitorFacade.isNativePlatform()) {
        await this.$auth.logout(); // ! clear browser session and revoke tokens
        await this.globalStore.resetStore();
        await this.$router.push({ name: RouteNames.POST_LOGOUT });
      } else {
        this.$auth.logout(); // ! clear browser session and revoke tokens
        await this.globalStore.resetStore();
      }
    },
  },
};

export default defineComponent(componentOptions);
</script>
